export type MeasurementSystem = "metric" | "imperial";
export const MEASUREMENT_SYSTEMS = ["metric", "imperial"] as const;
export const DEFAULT_MEASUREMENT_SYSTEM: MeasurementSystem = "metric";

export type DistanceUnit = "m" | "ft" | "mi";
export type SpeedUnit = "mph";
export type AreaUnit = "m2" | "ha" | "ft2" | "ac";
export type MeasurementUnit = DistanceUnit | SpeedUnit | AreaUnit;

export interface Quantity<U extends MeasurementUnit = MeasurementUnit> {
  value: string;
  unit: U;
}

export type HintSourceUnit = "m" | "km" | "kmh";

const METERS_PER_FOOT = 0.3048;
const KILOMETERS_PER_MILE = 1.609344;
const SQUARE_METERS_PER_SQUARE_FOOT = 0.09290304;
const SQUARE_METERS_PER_ACRE = 4046.8564224;
const SQUARE_METERS_PER_HECTARE = 10000;
const SQUARE_FEET_PER_ACRE = 43560;

export function isMeasurementSystem(
  value: unknown
): value is MeasurementSystem {
  return value === "metric" || value === "imperial";
}

export function resolveMeasurementSystem(
  cityValue: MeasurementSystem | null | undefined,
  orgValue: MeasurementSystem | null | undefined
): MeasurementSystem {
  return cityValue ?? orgValue ?? DEFAULT_MEASUREMENT_SYSTEM;
}

function roundTo(value: number, fractionDigits: number): number {
  const factor = 10 ** fractionDigits;
  return Math.round(value * factor) / factor;
}

// Rounding happens here, once. Intl only localises the already-rounded number: Intl's own
// half-way rounding is not guaranteed identical across engines, and toFixed rounds the
// binary value, so owning the rounding is what makes these outputs testable.
export function formatNumber(
  value: number,
  locale: string,
  fractionDigits: number
): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(roundTo(value, fractionDigits));
}

export function formatDistanceMeters(
  meters: number,
  system: MeasurementSystem,
  locale: string
): Quantity<"m" | "ft"> {
  if (system === "imperial") {
    return {
      value: formatNumber(meters / METERS_PER_FOOT, locale, 0),
      unit: "ft",
    };
  }
  return { value: formatNumber(meters, locale, 0), unit: "m" };
}

export function formatAreaSquareMeters(
  squareMeters: number,
  system: MeasurementSystem,
  locale: string
): Quantity<AreaUnit> {
  if (system === "imperial") {
    const squareFeet = squareMeters / SQUARE_METERS_PER_SQUARE_FOOT;
    if (squareFeet < SQUARE_FEET_PER_ACRE) {
      return { value: formatNumber(squareFeet, locale, 0), unit: "ft2" };
    }
    return {
      value: formatNumber(squareMeters / SQUARE_METERS_PER_ACRE, locale, 2),
      unit: "ac",
    };
  }
  if (squareMeters < SQUARE_METERS_PER_HECTARE) {
    return { value: formatNumber(squareMeters, locale, 0), unit: "m2" };
  }
  return {
    value: formatNumber(squareMeters / SQUARE_METERS_PER_HECTARE, locale, 2),
    unit: "ha",
  };
}

export function convertForHint(
  value: number | null | undefined,
  from: HintSourceUnit,
  system: MeasurementSystem,
  locale: string
): Quantity<"ft" | "mi" | "mph"> | null {
  if (system !== "imperial" || value == null || !Number.isFinite(value)) {
    return null;
  }
  switch (from) {
    case "m":
      return {
        value: formatNumber(value / METERS_PER_FOOT, locale, 0),
        unit: "ft",
      };
    case "km":
      return {
        value: formatNumber(value / KILOMETERS_PER_MILE, locale, 0),
        unit: "mi",
      };
    case "kmh":
      return {
        value: formatNumber(value / KILOMETERS_PER_MILE, locale, 0),
        unit: "mph",
      };
  }
}
